import React, { useEffect, useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import templateService from '../../../services/template.service';

/**
 * Loads clinic email template HTML via the API (S3 read server-side)
 * so private bucket objects can be previewed without making them public.
 */
function EmailTemplatePreviewModal({ show, clinicId, template, onClose }) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (!show || !clinicId || !template?.id) {
            return;
        }

        setIsLoading(true);
        setError(null);
        setPreview(null);

        templateService
            .getEmailTemplateById(clinicId, template.id)
            .then((res) => setPreview(res.data))
            .catch(() =>
                setError('Failed to load email preview. The template may be missing or unavailable.')
            )
            .finally(() => setIsLoading(false));
    }, [show, clinicId, template?.id]);

    const handleClose = () => {
        setPreview(null);
        setError(null);
        onClose();
    };

    const reminderName = preview?.reminderName || template?.reminderName || 'Email Template';
    const subjectLine = preview?.subjectLine || template?.subjectLine;
    const htmlContent = preview?.content;

    // Only treat the whole payload as a failed S3 path — HTML often embeds
    // amazonaws.com image URLs and must still render.
    const isUnresolvedStoragePath = (() => {
        if (!htmlContent || typeof htmlContent !== 'string') {
            return false;
        }
        const trimmed = htmlContent.trim();
        if (trimmed.startsWith('<')) {
            return false;
        }
        return (
            trimmed.startsWith('http://') ||
            trimmed.startsWith('https://') ||
            trimmed.startsWith('s3://')
        );
    })();

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="xl"
            centered
            backdrop="static"
            keyboard
            className="email-queue-preview-modal"
        >
            <Modal.Header closeButton={false}>
                <Modal.Title className="email-queue-preview-modal__title">
                    <i className="bi bi-envelope me-2"></i>
                    Email Preview
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                {isLoading && (
                    <div className="text-center py-5">
                        <Spinner animation="border" role="status" variant="primary" />
                        <p className="text-muted small mt-3 mb-0">Loading email content…</p>
                    </div>
                )}
                {!isLoading && error && (
                    <Alert variant="warning" className="m-3 mb-0">
                        {error}
                    </Alert>
                )}
                {!isLoading && preview && (
                    <>
                        <div className="px-3 py-2 border-bottom bg-light email-queue-preview-meta">
                            <div>
                                <strong>Template:</strong> {reminderName}
                            </div>
                            {subjectLine ? (
                                <div className="text-muted">
                                    <strong>Subject:</strong> {subjectLine}
                                </div>
                            ) : null}
                            <p className="text-danger email-queue-preview-note mb-0 mt-2">
                                Preview only. Editing will not save anything.
                            </p>
                        </div>
                        {isUnresolvedStoragePath ? (
                            <Alert variant="warning" className="m-3 mb-0">
                                Template content could not be loaded from storage.
                            </Alert>
                        ) : (
                            <iframe
                                title={`Preview of ${reminderName}`}
                                srcDoc={htmlContent || '<p>No content available.</p>'}
                                sandbox="allow-same-origin"
                                style={{
                                    width: '100%',
                                    minHeight: '70vh',
                                    border: 'none',
                                    display: 'block',
                                    background: '#f5f5f5',
                                }}
                            />
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={handleClose}>
                    <i className="bi bi-x-lg me-1"></i>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default EmailTemplatePreviewModal;
